/**
 * Image utility functions for handling both string URLs and Cloudinary object format.
 * Includes WebP auto-conversion for Cloudinary and Unsplash images.
 */

/**
 * Apply WebP transform to a Cloudinary URL.
 * Converts /upload/... to /upload/f_auto,q_auto/...
 */
function applyCloudinaryWebP(url, width) {
  if (!url || typeof url !== 'string') return url;

  // Cloudinary: inject f_auto,q_auto (auto-format including WebP + quality)
  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    const transforms = width ? `f_auto,q_auto,w_${width}` : 'f_auto,q_auto';
    // Avoid double-transforming
    if (url.includes('f_auto')) return url;
    return url.replace('/upload/', `/upload/${transforms}/`);
  }

  // Unsplash: append &fm=webp&q=80 and optional width
  if (url.includes('images.unsplash.com')) {
    const sep = url.includes('?') ? '&' : '?';
    let suffix = `${sep}fm=webp&q=80`;
    if (width) suffix += `&w=${width}`;
    // Avoid double-transforming
    if (url.includes('fm=webp')) return url;
    return url + suffix;
  }

  return url;
}

/**
 * Extract URL from image (supports both string and object formats).
 * Automatically applies WebP optimization for Cloudinary/Unsplash.
 */
export const getImageUrl = (image, useThumbnail = false, width) => {
  if (!image) return null;

  let url = null;

  if (typeof image === 'string') {
    url = image;
  } else if (typeof image === 'object') {
    if (useThumbnail && image.thumbnail_url) {
      url = image.thumbnail_url;
    } else {
      url = image.url || image.thumbnail_url || null;
    }
  }

  return url ? applyCloudinaryWebP(url, width) : null;
};

/**
 * Get the first image URL from an array of images.
 */
export const getFirstImageUrl = (images, useThumbnail = false) => {
  if (!images || !Array.isArray(images) || images.length === 0) return null;
  return getImageUrl(images[0], useThumbnail);
};

/**
 * Get all image URLs from an array.
 */
export const getAllImageUrls = (images, useThumbnail = false) => {
  if (!images || !Array.isArray(images)) return [];
  return images.map(img => getImageUrl(img, useThumbnail)).filter(Boolean);
};

/**
 * Get a srcSet string for responsive images (Cloudinary only).
 */
export const getResponsiveSrcSet = (image) => {
  const url = typeof image === 'string' ? image : image?.url;
  if (!url) return '';

  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    const widths = [320, 640, 960, 1280];
    return widths
      .map(w => `${applyCloudinaryWebP(url, w)} ${w}w`)
      .join(', ');
  }

  if (url.includes('images.unsplash.com')) {
    const widths = [320, 640, 960, 1280];
    return widths
      .map(w => `${applyCloudinaryWebP(url, w)} ${w}w`)
      .join(', ');
  }

  return '';
};
